import type { Point } from '../types';
import type { CategoryDef } from '../../../../helpers/api/config/config';
import {
  categoryHasPairedLengthAndPerimeter,
  findHcEllipseCategory,
  getLengthMeasForCategory,
  getPerimeterMeasForCategory,
  buildPerimeterMeasLabels,
} from '../../../../helpers/api/config/measConfig';
import {
  calculateEllipsePerimeter,
  calculateLineLength,
} from './shapeUtils';
import { getMeasurementDisplayName } from '../../../../constants/measurementLabels';

export type PairedBpdHc = {
  categoryId: number;
  ellipse: Record<string, unknown>;
  bpdLine: Record<string, unknown> | null;
  lengthMeas: string;
  perimeterMeas: string;
};

function measKey(label: unknown): string {
  return String(label ?? '')
    .trim()
    .split(/\s+/)[0];
}

function ellipseAreaScore(shape: Record<string, unknown>): number {
  const geom = getEllipseGeometryFromShape(shape);
  if (!geom) return 0;
  return geom.radiusX * geom.radiusY;
}

export function getEllipseGeometryFromShape(
  shape: Record<string, unknown>
): {
  center: Point;
  radiusX: number;
  radiusY: number;
  rotation?: number;
} | null {
  if (!hasEllipseGeometry(shape)) return null;
  return {
    center: shape.center as Point,
    radiusX: shape.radiusX as number,
    radiusY: shape.radiusY as number,
    rotation: (shape.rotation as number) ?? 0,
  };
}

export function buildDerivedBpdLineFromHcShape(
  hcShape: Record<string, unknown>,
  lengthMeas: string,
  categoryId: number,
  pixelSpacing?: { x: number; y: number } | null
): Record<string, unknown> | null {
  const geom = getEllipseGeometryFromShape(hcShape);
  if (!geom) return null;
  const pts = bpdLineFromEllipse(geom);
  const ellipseId = String(hcShape.id ?? '');
  return {
    id: `derived_bpd_${ellipseId}`,
    type: 'line',
    startPoint: pts.startPoint,
    endPoint: pts.endPoint,
    label: getMeasurementDisplayName(lengthMeas),
    backendLabel: lengthMeas,
    categoryId,
    segmentType: 'measurement',
    derivedFromShapeId: ellipseId,
    isManual: false,
    isNew: false,
    isDirty: false,
    color: hcShape.color ?? '#3b82f6',
    calculatedValue: calculateLineLength(
      pts.startPoint,
      pts.endPoint,
      pixelSpacing ?? undefined
    ),
    calculatedValueUnit: pixelSpacing ? 'cm' : 'px',
    showOverlayValue: true,
  };
}

function applyApiMeasurementToShape(
  shape: Record<string, unknown>,
  measName: string,
  frameMeasurements: Array<{ meas_name: string; value?: number; unit?: string }>
): void {
  const api = frameMeasurements.find((m) => m.meas_name === measName);
  if (api?.value == null) return;
  shape.calculatedValue = api.value;
  shape.calculatedValueUnit = api.unit ?? 'cm';
  shape.showOverlayValue = true;
}

export function shapeCategoryId(
  shape: Record<string, unknown>,
  measLabelToCategoryId: Record<string, number>
): number | undefined {
  if (shape.categoryId != null) {
    const n = Number(shape.categoryId);
    if (!isNaN(n)) return n;
  }
  const fromLabel = measLabelToCategoryId[measKey(shape.backendLabel)];
  return fromLabel ?? undefined;
}

/** BPD = minor axis diameter of the head ellipse (matches backend inference). */
export function bpdLineFromEllipse(circle: {
  center: Point;
  radiusX: number;
  radiusY: number;
  rotation?: number;
}): { startPoint: Point; endPoint: Point } {
  const { center, radiusX, radiusY, rotation = 0 } = circle;
  const useMinorAlongRotation = radiusX <= radiusY;
  const angle = useMinorAlongRotation ? rotation : rotation + Math.PI / 2;
  const r = Math.min(radiusX, radiusY);
  return {
    startPoint: {
      x: center.x - r * Math.cos(angle),
      y: center.y - r * Math.sin(angle),
    },
    endPoint: {
      x: center.x + r * Math.cos(angle),
      y: center.y + r * Math.sin(angle),
    },
  };
}

/** Update ellipse so its minor axis matches the BPD line (HC perimeter follows). */
export function syncEllipseFromBpdLine(
  ellipse: {
    center: Point;
    radiusX: number;
    radiusY: number;
    rotation?: number;
  },
  bpdLine: { startPoint: Point; endPoint: Point }
): {
  center: Point;
  radiusX: number;
  radiusY: number;
  rotation: number;
} {
  const { startPoint, endPoint } = bpdLine;
  const cx = (startPoint.x + endPoint.x) / 2;
  const cy = (startPoint.y + endPoint.y) / 2;
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  const newMinorR = Math.max(Math.hypot(dx, dy) / 2, 1);
  const lineAngle = Math.atan2(dy, dx);

  const { radiusX, radiusY } = ellipse;
  const minorWasRadiusX = radiusX <= radiusY;
  const majorR = Math.max(radiusX, radiusY, newMinorR);

  if (minorWasRadiusX) {
    return {
      center: { x: cx, y: cy },
      rotation: lineAngle,
      radiusX: newMinorR,
      radiusY: majorR,
    };
  }
  return {
    center: { x: cx, y: cy },
    rotation: lineAngle - Math.PI / 2,
    radiusY: newMinorR,
    radiusX: majorR,
  };
}

function findBpdLineForEllipse(
  shapes: Record<string, unknown>[],
  ellipse: Record<string, unknown>,
  catId: number,
  lengthMeas: string,
  measLabelToCategoryId: Record<string, number>
): Record<string, unknown> | null {
  const ellipseId = String(ellipse.id ?? '');
  return (
    shapes.find(
      (s) => s.type === 'line' && String(s.derivedFromShapeId ?? '') === ellipseId
    ) ??
    shapes.find(
      (s) =>
        s.type === 'line' &&
        measKey(s.backendLabel) === lengthMeas &&
        shapeCategoryId(s, measLabelToCategoryId) === catId
    ) ??
    shapes.find(
      (s) => s.type === 'line' && measKey(s.backendLabel) === lengthMeas
    ) ??
    null
  );
}

function findEllipseForBpdLine(
  shapes: Record<string, unknown>[],
  bpdLine: Record<string, unknown>,
  catId: number | undefined,
  perimeterMeas: string,
  measLabelToCategoryId: Record<string, number>
): Record<string, unknown> | null {
  const linkedId = bpdLine.derivedFromShapeId
    ? String(bpdLine.derivedFromShapeId)
    : null;
  if (linkedId) {
    const linked = shapes.find((s) => String(s.id) === linkedId);
    if (linked?.type === 'circle') return linked;
  }
  if (catId != null) {
    const inCat = shapes.find(
      (s) =>
        s.type === 'circle' &&
        shapeCategoryId(s, measLabelToCategoryId) === catId
    );
    if (inCat) return inCat;
  }
  return (
    shapes.find(
      (s) =>
        s.type === 'circle' && measKey(s.backendLabel) === perimeterMeas
    ) ?? shapes.find((s) => s.type === 'circle') ?? null
  );
}

export function findPairedBpdHcInShapes(
  shapes: Record<string, unknown>[],
  categories: CategoryDef[],
  measLabelToCategoryId: Record<string, number> = {}
): PairedBpdHc[] {
  const pairs: PairedBpdHc[] = [];
  const seenEllipseIds = new Set<string>();

  const tryAddPair = (
    ellipse: Record<string, unknown>,
    bpdLine: Record<string, unknown> | null,
    catId: number,
    lengthMeas: string,
    perimeterMeas: string
  ) => {
    if (!ellipse?.id) return;
    const ellipseId = String(ellipse.id);
    if (seenEllipseIds.has(ellipseId)) return;
    seenEllipseIds.add(ellipseId);
    pairs.push({ categoryId: catId, ellipse, bpdLine, lengthMeas, perimeterMeas });
  };

  for (const cat of categories) {
    if (!categoryHasPairedLengthAndPerimeter(cat)) continue;
    const lengthMeas = getLengthMeasForCategory(cat);
    const perimeterMeas = getPerimeterMeasForCategory(cat);
    if (!lengthMeas || !perimeterMeas) continue;

    const catId = cat.id;
    const ellipses = shapes.filter(
      (s) =>
        s.type === 'circle' &&
        shapeCategoryId(s, measLabelToCategoryId) === catId
    );
    const ellipse =
      ellipses.find((s) => measKey(s.backendLabel) === perimeterMeas) ??
      ellipses
        .slice()
        .sort((a, b) => ellipseAreaScore(b) - ellipseAreaScore(a))[0];
    if (!ellipse) continue;

    const bpdLine = findBpdLineForEllipse(
      shapes,
      ellipse,
      catId,
      lengthMeas,
      measLabelToCategoryId
    );
    tryAddPair(ellipse, bpdLine, catId, lengthMeas, perimeterMeas);
  }

  // Fallback: structure ellipse for HC category (e.g. fetal_brain) without meas label
  const hcCategory = findHcEllipseCategory(categories);
  for (const s of shapes) {
    if (!s?.id || seenEllipseIds.has(String(s.id))) continue;
    if (s.type !== 'circle' || !hasEllipseGeometry(s)) continue;
    const catId = Number(s.categoryId);
    const label = String(s.label ?? s.backendLabel ?? '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/_not_visible$/i, '');
    const hcCategoryId = hcCategory?.id;
    const isHcStructure =
      (hcCategoryId != null && catId === hcCategoryId) ||
      label === 'fetal_brain' ||
      (hcCategory != null && label === hcCategory.code.toLowerCase());
    if (!isHcStructure) continue;

    const cat = hcCategory ?? categories.find((c) => categoryHasPairedLengthAndPerimeter(c));
    const lengthMeas = cat ? getLengthMeasForCategory(cat) : null;
    const perimeterMeas = cat ? getPerimeterMeasForCategory(cat) : null;
    if (!lengthMeas || !perimeterMeas || !cat) continue;

    const bpdLine = findBpdLineForEllipse(
      shapes,
      s,
      cat.id,
      lengthMeas,
      measLabelToCategoryId
    );
    tryAddPair(s, bpdLine, cat.id, lengthMeas, perimeterMeas);
  }

  // Fallback: MEAS__HC ellipse without category_id
  for (const s of shapes) {
    if (!s?.id || seenEllipseIds.has(String(s.id))) continue;
    if (measKey(s.backendLabel) !== 'MEAS__HC' || !hasEllipseGeometry(s)) continue;
    const cat =
      findHcEllipseCategory(categories) ??
      categories.find(
        (c) =>
          categoryHasPairedLengthAndPerimeter(c) &&
          getPerimeterMeasForCategory(c) === 'MEAS__HC'
      );
    if (!cat) continue;
    const lengthMeas = getLengthMeasForCategory(cat);
    const perimeterMeas = getPerimeterMeasForCategory(cat);
    if (!lengthMeas || !perimeterMeas) continue;
    const bpdLine = findBpdLineForEllipse(
      shapes,
      s,
      cat.id,
      lengthMeas,
      measLabelToCategoryId
    );
    tryAddPair(s, bpdLine, cat.id, lengthMeas, perimeterMeas);
  }

  // Fallback: MEAS__BPD line + any circle on frame
  const fallbackLengthMeas = hcCategory
    ? getLengthMeasForCategory(hcCategory)
    : null;
  const fallbackPerimeterMeas = hcCategory
    ? getPerimeterMeasForCategory(hcCategory)
    : null;
  const orphanBpdLines = shapes.filter((s) => {
    if (s.type !== 'line') return false;
    const key = measKey(s.backendLabel);
    if (fallbackLengthMeas && key === fallbackLengthMeas) return true;
    return key === 'MEAS__BPD';
  });
  for (const bpdLine of orphanBpdLines) {
    const catId = shapeCategoryId(bpdLine, measLabelToCategoryId);
    const ellipse = findEllipseForBpdLine(
      shapes,
      bpdLine,
      catId,
      fallbackPerimeterMeas ?? 'MEAS__HC',
      measLabelToCategoryId
    );
    if (ellipse && fallbackLengthMeas && fallbackPerimeterMeas) {
      tryAddPair(
        ellipse,
        bpdLine,
        catId ?? shapeCategoryId(ellipse, measLabelToCategoryId) ?? hcCategory?.id ?? 0,
        fallbackLengthMeas,
        fallbackPerimeterMeas
      );
    }
  }

  return pairs;
}

export function findPairContainingShapeId(
  shapes: Record<string, unknown>[],
  shapeId: string,
  categories: CategoryDef[],
  measLabelToCategoryId: Record<string, number>
): PairedBpdHc | null {
  for (const pair of findPairedBpdHcInShapes(
    shapes,
    categories,
    measLabelToCategoryId
  )) {
    const ellipseId = String(pair.ellipse.id);
    const bpdId = pair.bpdLine?.id ? String(pair.bpdLine.id) : null;
    if (shapeId === ellipseId || shapeId === bpdId) return pair;
  }
  return null;
}

export function findLinkedBpdLineIds(
  shapes: Array<{ id?: string; type?: string; derivedFromShapeId?: string }>,
  ellipseId: string
): string[] {
  return shapes
    .filter(
      (s) => s.type === 'line' && s.derivedFromShapeId === ellipseId && s.id
    )
    .map((s) => String(s.id));
}

function hasLineGeometry(shape: Record<string, unknown>): boolean {
  const sp = shape.startPoint as Point | undefined;
  const ep = shape.endPoint as Point | undefined;
  return Boolean(sp && ep);
}

function hasEllipseGeometry(shape: Record<string, unknown>): boolean {
  return Boolean(
    shape.type === 'circle' &&
      shape.center &&
      typeof shape.radiusX === 'number' &&
      typeof shape.radiusY === 'number'
  );
}

/**
 * Return patches for the *linked* shape only (not the shape the user edited).
 * Caller must apply the primary edit separately.
 */
export function buildLinkedBpdHcPatch(
  shapes: Record<string, unknown>[],
  editedShapeId: string,
  editedShape: Record<string, unknown>,
  categories: CategoryDef[],
  measLabelToCategoryId: Record<string, number>,
  pixelSpacing?: { x: number; y: number } | null,
  pixelSpacingUnit?: string
): Map<string, Record<string, unknown>> {
  const linked = new Map<string, Record<string, unknown>>();
  const pair = findPairContainingShapeId(
    shapes,
    editedShapeId,
    categories,
    measLabelToCategoryId
  );
  if (!pair) return linked;

  const ellipseId = String(pair.ellipse.id);
  const bpdId = pair.bpdLine?.id ? String(pair.bpdLine.id) : null;

  if (editedShapeId === ellipseId && bpdId && hasEllipseGeometry(editedShape)) {
    const pts = bpdLineFromEllipse(editedShape as any);
    linked.set(bpdId, {
      startPoint: pts.startPoint,
      endPoint: pts.endPoint,
      derivedFromShapeId: ellipseId,
      backendLabel: pair.lengthMeas,
      categoryId: pair.categoryId,
      calculatedValue: calculateLineLength(
        pts.startPoint,
        pts.endPoint,
        pixelSpacing ?? undefined
      ),
      showOverlayValue: true,
      isManual: true,
      isDirty: true,
    });
  } else if (
    bpdId &&
    editedShapeId === bpdId &&
    hasLineGeometry(editedShape)
  ) {
    const baseEllipse = pair.ellipse;
    if (!hasEllipseGeometry(baseEllipse)) return linked;

    const ellipseUpdates = syncEllipseFromBpdLine(baseEllipse as any, {
      startPoint: editedShape.startPoint as Point,
      endPoint: editedShape.endPoint as Point,
    });
    linked.set(ellipseId, {
      ...ellipseUpdates,
      backendLabel: pair.perimeterMeas,
      categoryId: pair.categoryId,
      calculatedValue: calculateEllipsePerimeter(
        ellipseUpdates.radiusX,
        ellipseUpdates.radiusY,
        pixelSpacing ?? undefined,
        pixelSpacingUnit
      ),
      showOverlayValue: true,
      isManual: true,
      isDirty: true,
    });
  }

  return linked;
}

/** Recompute overlay values from current shape geometry (not stale API measurements). */
export function refreshMeasurementOverlaysFromGeometry(
  shapes: Record<string, unknown>[],
  pixelSpacing?: { x: number; y: number } | null,
  pixelSpacingUnit?: string
): void {
  for (const shape of shapes) {
    const meas = measKey(shape.backendLabel);
    if (!meas.startsWith('MEAS__')) continue;

    if (shape.type === 'line' && hasLineGeometry(shape)) {
      const sp = shape.startPoint as Point;
      const ep = shape.endPoint as Point;
      shape.calculatedValue = calculateLineLength(sp, ep, pixelSpacing ?? undefined);
      shape.calculatedValueUnit = pixelSpacing ? 'cm' : 'px';
      shape.showOverlayValue = true;
    } else if (hasEllipseGeometry(shape)) {
      shape.calculatedValue = calculateEllipsePerimeter(
        shape.radiusX as number,
        shape.radiusY as number,
        pixelSpacing ?? undefined,
        pixelSpacingUnit
      );
      shape.calculatedValueUnit = pixelSpacing ? 'cm' : 'px';
      shape.showOverlayValue = true;
    }
  }
}

/** Live meas map for sidebar sync while editing on canvas. */
export function buildLiveMeasurementsFromShapes(
  shapes: Array<Record<string, unknown>>,
  pixelSpacing?: { x: number; y: number } | null,
  pixelSpacingUnit?: string,
  categories: CategoryDef[] = [],
  measLabelToCategoryId: Record<string, number> = {}
): Record<string, { value: number; unit: string }> {
  const out: Record<string, { value: number; unit: string }> = {};
  for (const shape of shapes) {
    const meas = measKey(shape.backendLabel);
    if (!meas.startsWith('MEAS__')) continue;

    if (shape.type === 'line' && hasLineGeometry(shape)) {
      const sp = shape.startPoint as Point;
      const ep = shape.endPoint as Point;
      out[meas] = {
        value: calculateLineLength(sp, ep, pixelSpacing ?? undefined),
        unit: pixelSpacing ? 'cm' : 'px',
      };
    } else if (hasEllipseGeometry(shape)) {
      out[meas] = {
        value: calculateEllipsePerimeter(
          shape.radiusX as number,
          shape.radiusY as number,
          pixelSpacing ?? undefined,
          pixelSpacingUnit
        ),
        unit: pixelSpacing ? 'cm' : 'px',
      };
    } else if (typeof shape.calculatedValue === 'number') {
      out[meas] = {
        value: shape.calculatedValue,
        unit: String(shape.calculatedValueUnit ?? (pixelSpacing ? 'cm' : 'px')),
      };
    }
  }

  // Structure HC ellipses (fetal_brain) without MEAS__ label yet
  if (categories.length) {
    for (const pair of findPairedBpdHcInShapes(
      shapes,
      categories,
      measLabelToCategoryId
    )) {
      if (out[pair.perimeterMeas]) continue;
      if (!hasEllipseGeometry(pair.ellipse)) continue;
      out[pair.perimeterMeas] = {
        value: calculateEllipsePerimeter(
          pair.ellipse.radiusX as number,
          pair.ellipse.radiusY as number,
          pixelSpacing ?? undefined,
          pixelSpacingUnit
        ),
        unit: pixelSpacing ? 'cm' : 'px',
      };
      if (pair.bpdLine && hasLineGeometry(pair.bpdLine) && !out[pair.lengthMeas]) {
        out[pair.lengthMeas] = {
          value: calculateLineLength(
            pair.bpdLine.startPoint as Point,
            pair.bpdLine.endPoint as Point,
            pixelSpacing ?? undefined
          ),
          unit: pixelSpacing ? 'cm' : 'px',
        };
      }
    }
  }

  return out;
}

/** BPD lines that should exist for HC ellipses on canvas but are missing. */
export function collectMissingDerivedBpdLines(
  shapes: Record<string, unknown>[],
  categories: CategoryDef[],
  measLabelToCategoryId: Record<string, number> = {},
  pixelSpacing?: { x: number; y: number } | null
): Record<string, unknown>[] {
  const missing: Record<string, unknown>[] = [];
  const existingIds = new Set(
    shapes.filter((s) => s.type === 'line').map((s) => String(s.id ?? ''))
  );

  for (const pair of findPairedBpdHcInShapes(
    shapes,
    categories,
    measLabelToCategoryId
  )) {
    const ellipseId = String(pair.ellipse.id ?? '');
    const hasBpdLine = shapes.some(
      (s) =>
        s.type === 'line' &&
        hasLineGeometry(s) &&
        (measKey(s.backendLabel) === pair.lengthMeas ||
          String(s.derivedFromShapeId ?? '') === ellipseId)
    );
    if (hasBpdLine) continue;
    if (!hasEllipseGeometry(pair.ellipse)) continue;
    const derived = buildDerivedBpdLineFromHcShape(
      pair.ellipse,
      pair.lengthMeas,
      pair.categoryId,
      pixelSpacing
    );
    if (!derived?.id || existingIds.has(String(derived.id))) continue;
    existingIds.add(String(derived.id));
    missing.push(derived);
  }
  return missing;
}

/** Rebuild HC ellipse when only the BPD line was saved (legacy overwrite bug). */
function reconstructHcEllipseFromBpdLine(
  bpdLine: Record<string, unknown>,
  categoryId: number,
  perimeterMeas: string,
  hcPerimeterCm: number | undefined,
  pixelSpacing?: { x: number; y: number } | null,
  pixelSpacingUnit?: string
): Record<string, unknown> | null {
  if (!hasLineGeometry(bpdLine)) return null;
  const sp = bpdLine.startPoint as Point;
  const ep = bpdLine.endPoint as Point;
  const cx = (sp.x + ep.x) / 2;
  const cy = (sp.y + ep.y) / 2;
  const minorR = Math.max(Math.hypot(ep.x - sp.x, ep.y - sp.y) / 2, 1);
  const rotation = Math.atan2(ep.y - sp.y, ep.x - sp.x);

  let majorR = Math.max(minorR * 1.75, minorR + 1);
  if (hcPerimeterCm != null && pixelSpacing) {
    for (let i = 0; i < 24; i++) {
      const p = calculateEllipsePerimeter(
        minorR,
        majorR,
        pixelSpacing,
        pixelSpacingUnit
      );
      if (Math.abs(p - hcPerimeterCm) < 0.1) break;
      majorR *= p > hcPerimeterCm ? 0.92 : 1.08;
    }
  }

  const bpdId = String(bpdLine.id ?? 'bpd');
  return {
    id: `reconstructed_hc_${bpdId}`,
    type: 'circle',
    center: { x: cx, y: cy },
    radiusX: minorR,
    radiusY: majorR,
    rotation,
    label: getMeasurementDisplayName(perimeterMeas),
    backendLabel: perimeterMeas,
    categoryId,
    segmentType: 'measurement',
    isManual: false,
    isNew: false,
    isDirty: false,
    color: bpdLine.color ?? '#3b82f6',
    showOverlayValue: hcPerimeterCm != null,
    calculatedValue: hcPerimeterCm,
    calculatedValueUnit: hcPerimeterCm != null ? 'cm' : undefined,
  };
}

function ensureHcEllipseWhenOnlyBpdLineSaved(
  shapes: Record<string, unknown>[],
  categories: CategoryDef[],
  frameMeasurements: Array<{ meas_name: string; value?: number; unit?: string }>,
  pixelSpacing?: { x: number; y: number } | null,
  pixelSpacingUnit?: string,
  measLabelToCategoryId: Record<string, number> = {}
): void {
  const hcCategory = findHcEllipseCategory(categories);
  if (!hcCategory) return;

  const lengthMeas = getLengthMeasForCategory(hcCategory);
  const perimeterMeas = getPerimeterMeasForCategory(hcCategory);
  if (!lengthMeas || !perimeterMeas) return;

  const catId = hcCategory.id;
  const hasHcEllipse = shapes.some(
    (s) =>
      s.type === 'circle' &&
      (shapeCategoryId(s, measLabelToCategoryId) === catId ||
        measKey(s.backendLabel) === perimeterMeas)
  );
  if (hasHcEllipse) return;

  const bpdLine =
    shapes.find(
      (s) =>
        s.type === 'line' &&
        (measKey(s.backendLabel) === lengthMeas ||
          String(s.id ?? '').startsWith('derived_bpd_'))
    ) ??
    shapes.find((s) => s.type === 'line' && measKey(s.backendLabel) === 'MEAS__BPD') ??
    shapes.find(
      (s) => s.type === 'line' && shapeCategoryId(s, measLabelToCategoryId) === catId
    );

  if (!bpdLine) return;

  const hcApi = frameMeasurements.find((m) => m.meas_name === perimeterMeas);
  const reconstructed = reconstructHcEllipseFromBpdLine(
    bpdLine,
    catId,
    perimeterMeas,
    hcApi?.value,
    pixelSpacing,
    pixelSpacingUnit
  );
  if (reconstructed) {
    bpdLine.derivedFromShapeId = String(reconstructed.id);
    shapes.push(reconstructed);
  }
}

function scoreBpdLineCandidate(line: Record<string, unknown>): number {
  let score = 0;
  if (line.isManual === true) score += 100;
  if (line.dbId != null) score += 50;
  if (line.derivedFromShapeId) score += 10;
  if (String(line.id ?? '').startsWith('derived_bpd_')) score -= 5;
  return score;
}

/** Drop extra BPD lines in the same head slot (user + AI duplicates on canvas). */
function pruneDuplicateBpdLines(
  shapes: Record<string, unknown>[],
  categories: CategoryDef[],
  measLabelToCategoryId: Record<string, number> = {}
): void {
  const hcCategory = findHcEllipseCategory(categories);
  if (!hcCategory) return;
  const lengthMeas = getLengthMeasForCategory(hcCategory);
  if (!lengthMeas) return;
  const catId = hcCategory.id;
  const keepIds = new Set<string>();

  for (const pair of findPairedBpdHcInShapes(
    shapes,
    categories,
    measLabelToCategoryId
  )) {
    const ellipseId = String(pair.ellipse.id ?? '');
    const candidates = shapes.filter(
      (s) =>
        s.type === 'line' &&
        hasLineGeometry(s) &&
        (measKey(s.backendLabel) === lengthMeas ||
          measKey(s.backendLabel) === 'MEAS__BPD' ||
          String(s.derivedFromShapeId ?? '') === ellipseId ||
          shapeCategoryId(s, measLabelToCategoryId) === catId)
    );
    if (!candidates.length) continue;
    candidates.sort(
      (a, b) => scoreBpdLineCandidate(b) - scoreBpdLineCandidate(a)
    );
    keepIds.add(String(candidates[0].id));
  }

  if (!keepIds.size) return;

  for (let i = shapes.length - 1; i >= 0; i--) {
    const s = shapes[i];
    if (s.type !== 'line' || !hasLineGeometry(s)) continue;
    const meas = measKey(s.backendLabel);
    const isBpd =
      meas === lengthMeas ||
      meas === 'MEAS__BPD' ||
      shapeCategoryId(s, measLabelToCategoryId) === catId;
    if (!isBpd) continue;
    if (!keepIds.has(String(s.id))) {
      shapes.splice(i, 1);
    }
  }
}

/** After load: link BPD line ↔ HC ellipse; keep saved geometry, only create BPD if missing. */
export function linkAndSyncDerivedBpdShapes(
  shapes: Record<string, unknown>[],
  categories: CategoryDef[],
  frameMeasurements: Array<{ meas_name: string; value?: number; unit?: string }> = [],
  pixelSpacing?: { x: number; y: number } | null,
  pixelSpacingUnit?: string,
  measLabelToCategoryId: Record<string, number> = {}
): Record<string, unknown>[] {
  ensureHcEllipseWhenOnlyBpdLineSaved(
    shapes,
    categories,
    frameMeasurements,
    pixelSpacing,
    pixelSpacingUnit,
    measLabelToCategoryId
  );

  pruneDuplicateBpdLines(shapes, categories, measLabelToCategoryId);

  for (const pair of findPairedBpdHcInShapes(
    shapes,
    categories,
    measLabelToCategoryId
  )) {
    const ellipse = pair.ellipse;
    const ellipseId = String(ellipse.id);
    ellipse.categoryId = pair.categoryId;
    if (!measKey(ellipse.backendLabel).startsWith('MEAS__')) {
      ellipse.backendLabel = pair.perimeterMeas;
    }

    let bpdLine = pair.bpdLine;
    if (bpdLine && hasLineGeometry(bpdLine)) {
      bpdLine.categoryId = pair.categoryId;
      bpdLine.backendLabel = pair.lengthMeas;
      bpdLine.derivedFromShapeId = ellipseId;
      bpdLine.segmentType = 'measurement';
      bpdLine.label = getMeasurementDisplayName(pair.lengthMeas);
    } else if (hasEllipseGeometry(ellipse)) {
      const pts = bpdLineFromEllipse(ellipse as any);
      if (bpdLine) {
        bpdLine.startPoint = pts.startPoint;
        bpdLine.endPoint = pts.endPoint;
        bpdLine.backendLabel = pair.lengthMeas;
        bpdLine.derivedFromShapeId = ellipseId;
        bpdLine.segmentType = 'measurement';
        bpdLine.label = getMeasurementDisplayName(pair.lengthMeas);
      } else {
        const derived = buildDerivedBpdLineFromHcShape(
          ellipse,
          pair.lengthMeas,
          pair.categoryId,
          pixelSpacing
        );
        if (derived) shapes.push(derived);
      }
    }
  }

  refreshMeasurementOverlaysFromGeometry(shapes, pixelSpacing, pixelSpacingUnit);
  const perimeterMeasLabels = buildPerimeterMeasLabels(categories);
  for (const shape of shapes) {
    const meas = measKey(shape.backendLabel);
    if (!meas.startsWith('MEAS__')) continue;
    // Drawable geometry is authoritative — API values may be stale or use wrong units.
    if (perimeterMeasLabels.has(meas)) continue;
    if (shape.type === 'line' && hasLineGeometry(shape)) continue;
    if (hasEllipseGeometry(shape)) continue;
    applyApiMeasurementToShape(shape, meas, frameMeasurements);
  }
  return shapes;
}
